import { Controller } from "@hotwired/stimulus"
import { onMorph } from "../../lib/reactor"

export default class extends Controller {
  static targets = ["list", "input", "values", "chipTemplate"]
  static values = {
    paramName: String
  }

  connect() {
    onMorph(this)
    this.#ensureEmptyValueIfNeeded()
  }

  morph() {
    this.#ensureEmptyValueIfNeeded()
  }

  keydown(event) {
    const pending = this.inputTarget.value.trim() !== ""

    if (event.key === "," || ((event.key === "Enter" || event.key === "Tab") && pending)) {
      event.preventDefault()
      this.#commitInput()
    } else if (event.key === "Backspace" && this.inputTarget.value === "") {
      this.#removeLastChip()
    }
  }

  blur() {
    this.#commitInput()
  }

  remove(event) {
    const chip = event.currentTarget.closest("[data-queue-chip]")
    if (!chip) return
    this.#dropChip(chip)
    this.inputTarget.focus()
  }

  #commitInput() {
    const raw = this.inputTarget.value
    const parts = raw.split(",").map((part) => part.trim()).filter(Boolean)
    if (parts.length === 0) {
      this.inputTarget.value = ""
      return
    }

    for (const name of parts) {
      if (!this.#hasChip(name)) this.#addChip(name)
    }
    this.inputTarget.value = ""
    this.#ensureEmptyValueIfNeeded()
  }

  #hasChip(name) {
    return Array.from(this.listTarget.querySelectorAll("[data-queue-chip]")).some(
      (chip) => chip.dataset.queueName === name
    )
  }

  #addChip(name) {
    this.#clearPlaceholderValues()

    const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true)
    chip.dataset.queueName = name
    chip.querySelector("[data-queue-label]").textContent = name
    chip.querySelector("button").setAttribute("aria-label", `Remove ${name}`)
    this.listTarget.appendChild(chip)
    this.valuesTarget.appendChild(this.#buildHidden(name))
  }

  #removeLastChip() {
    const chips = this.listTarget.querySelectorAll("[data-queue-chip]")
    if (chips.length === 0) return
    this.#dropChip(chips[chips.length - 1])
  }

  #dropChip(chip) {
    const name = chip.dataset.queueName
    chip.remove()
    this.#removeValue(name)
    this.#ensureEmptyValueIfNeeded()
  }

  #removeValue(name) {
    this.#hiddenInputs()
      .filter((input) => input.value === name)
      .forEach((input) => input.remove())
  }

  #clearPlaceholderValues() {
    this.#hiddenInputs()
      .filter((input) => input.value === "")
      .forEach((input) => input.remove())
  }

  #ensureEmptyValueIfNeeded() {
    const inputs = this.#hiddenInputs()
    const real = inputs.filter((input) => input.value !== "")
    const placeholders = inputs.filter((input) => input.value === "")

    if (real.length > 0) {
      placeholders.forEach((input) => input.remove())
      return
    }

    if (placeholders.length === 0) {
      this.valuesTarget.appendChild(this.#buildHidden(""))
    } else if (placeholders.length > 1) {
      placeholders.slice(1).forEach((input) => input.remove())
    }
  }

  #hiddenInputs() {
    return Array.from(this.valuesTarget.querySelectorAll("input[type='hidden']"))
  }

  #buildHidden(value) {
    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = this.paramNameValue
    hidden.value = value
    return hidden
  }
}
